// widgets/ReadinessWidget.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Zap, Battery, Moon, Heart, TrendingUp, AlertTriangle, CheckCircle, Activity } from "lucide-react";

const ReadinessWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const readinessTab = tabs.find((tab) => tab.id === "readiness");
    const widget = readinessTab?.widget || {};

    // Get readiness level color
    const getReadinessColor = (score: number) => {
        if (score >= 80) return { gradient: "from-green-400 to-emerald-500", text: "text-green-700", bg: "bg-green-50" };
        if (score >= 60) return { gradient: "from-yellow-400 to-amber-500", text: "text-yellow-700", bg: "bg-yellow-50" };
        return { gradient: "from-red-400 to-rose-500", text: "text-red-700", bg: "bg-red-50" };
    };

    const colors = getReadinessColor(widget.score || 0);

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/90 via-cyan-50/30 to-blue-100/20 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-cyan-200/40 mb-4 overflow-hidden relative">
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-cyan-200 to-blue-100 rounded-full opacity-20 blur-2xl`}></div>
                <div className={`absolute ${isRTL ? "right-10" : "left-10"} -bottom-10 w-40 h-40 bg-gradient-to-br from-blue-200 to-cyan-100 rounded-full opacity-20 blur-2xl`}></div>

                {/* Header */}
                <div className="flex justify-between items-center mb-6 relative z-10">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl shadow-lg`}
                            animate={{
                                rotate: [0, 10, -10, 0],
                                scale: [1, 1.1, 1],
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                            }}
                        >
                            <Zap className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                            <h3 className="text-xl font-bold text-cyan-900 mb-1">{widget.title}</h3>
                            <p className="text-sm text-cyan-600">{widget.subtitle}</p>
                        </div>
                    </div>
                </div>

                {/* Readiness score display */}
                <div className="relative mb-6">
                    <motion.div
                        className="flex items-center justify-center p-8 bg-white/60 backdrop-blur-md rounded-3xl border border-cyan-200/50 shadow-lg"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        {/* Circular progress */}
                        <div className="relative">
                            <svg width="180" height="180" viewBox="0 0 180 180">
                                {/* Background circle */}
                                <circle cx="90" cy="90" r="70" fill="none" stroke="#e5e7eb" strokeWidth="12" />

                                {/* Gradient definition */}
                                <defs>
                                    <linearGradient id="readinessGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" className={`${colors.gradient.split(" ")[0].replace("from-", "")}`} stopColor="#22d3ee" />
                                        <stop offset="100%" className={`${colors.gradient.split(" ")[1].replace("to-", "")}`} stopColor="#3b82f6" />
                                    </linearGradient>
                                </defs>

                                {/* Progress circle */}
                                <motion.circle
                                    cx="90"
                                    cy="90"
                                    r="70"
                                    fill="none"
                                    stroke="url(#readinessGradient)"
                                    strokeWidth="12"
                                    strokeLinecap="round"
                                    strokeDasharray={`${2 * Math.PI * 70}`}
                                    initial={{ strokeDashoffset: 2 * Math.PI * 70 }}
                                    animate={{ strokeDashoffset: 2 * Math.PI * 70 * (1 - (widget.score || 0) / 100) }}
                                    transition={{ duration: 2, ease: "easeInOut" }}
                                    style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
                                />
                            </svg>

                            {/* Center content */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <motion.div
                                    className={`text-5xl font-bold bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
                                >
                                    {widget.score}
                                </motion.div>
                                <p className="text-sm text-gray-600 mt-2">{widget.scoreLabel}</p>
                                <motion.div
                                    className={`mt-3 px-4 py-1.5 rounded-full ${colors.bg} ${colors.text} text-sm font-semibold`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.8 }}
                                >
                                    {widget.level}
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Key metrics */}
                <div className="grid grid-cols-3 gap-3 mb-6 relative z-10">
                    {widget.metrics?.map((metric: any, index: number) => (
                        <motion.div
                            key={index}
                            className="p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-cyan-100/50 text-center"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 + index * 0.1 }}
                            whileHover={{ scale: 1.05, y: -3 }}
                        >
                            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br ${metric.color} text-white mb-2`}>
                                {metric.icon === "moon" && <Moon className="w-5 h-5" />}
                                {metric.icon === "heart" && <Heart className="w-5 h-5" />}
                                {metric.icon === "activity" && <Activity className="w-5 h-5" />}
                            </div>
                            <div className="text-lg font-bold text-gray-800">{metric.value}</div>
                            <div className="text-xs text-gray-600">{metric.label}</div>
                        </motion.div>
                    ))}
                </div>

                {/* Contributing factors */}
                <div className="space-y-3 relative z-10">
                    <h4 className="text-sm font-semibold text-cyan-900 mb-3 flex items-center">
                        <Battery className={`w-4 h-4 me-2 text-cyan-600`} />
                        {widget.factors?.title}
                    </h4>

                    {widget.factors?.items?.map((factor: any, index: number) => (
                        <motion.div
                            key={index}
                            className="flex items-center justify-between p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-cyan-100/50"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                        >
                            <div className="flex items-center gap-3 flex-1">
                                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${factor.color} flex items-center justify-center`}>
                                    {factor.status === "good" && <CheckCircle className="w-4 h-4 text-white" />}
                                    {factor.status === "warning" && <AlertTriangle className="w-4 h-4 text-white" />}
                                    {factor.status === "normal" && <TrendingUp className="w-4 h-4 text-white" />}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-800">{factor.label}</span>
                                        <span className={`text-xs font-semibold ${factor.status === "good" ? "text-green-600" : factor.status === "warning" ? "text-yellow-600" : "text-gray-600"}`}>
                                            {factor.value}
                                        </span>
                                    </div>
                                    <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                                        <motion.div
                                            className={`h-full bg-gradient-to-r ${factor.color}`}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${factor.percentage}%` }}
                                            transition={{ duration: 1, delay: 1 + index * 0.1 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Recommendations */}
            <motion.div
                className="bg-gradient-to-br from-cyan-50/60 to-blue-50/40 backdrop-blur-xl rounded-2xl p-4 border border-cyan-200/50 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
            >
                <div className="flex items-center mb-3">
                    <div className={`w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center me-2 shadow-lg`}>
                        <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h4 className="font-bold text-cyan-900">{widget.recommendations?.title}</h4>
                        <p className="text-xs text-cyan-600">{widget.recommendations?.subtitle}</p>
                    </div>
                </div>

                <div className="space-y-2">
                    {widget.recommendations?.items?.map((rec: string, index: number) => (
                        <motion.div
                            key={index}
                            className="flex items-start gap-2 p-2 bg-white/60 backdrop-blur-sm rounded-lg"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                        >
                            <div className="w-5 h-5 mt-0.5 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="w-3 h-3 text-cyan-600" />
                            </div>
                            <p className="text-sm text-gray-700">{rec}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ReadinessWidget;
